import { gql, useApolloClient } from '@provision/graphql-client';
import { useEffect, useMemo, useState } from 'react';
import type { PlatformUserRaw } from 'src/types';

export const PLATFORM_USER_AVATAR_FRAGMENT = gql`
  fragment PlatformUserAvatarFields on PlatformUser {
    id
    partnerId
    avatarFileKey
  }
`;

export type PlatformUserAvatarFields = Pick<PlatformUserRaw, 'partnerId' | 'avatarFileKey'>;

export function usePlatformUserAvatarLookup(
  userIds: readonly (string | undefined | null)[],
): Map<string, PlatformUserAvatarFields | undefined> {
  const apolloClient = useApolloClient();

  // Stable string key over the unique-id set. Re-subscribing only when the actual set
  // of ids changes, not on every render.
  const userIdsKey = Array.from(new Set(userIds.filter((id): id is string => Boolean(id))))
    .sort()
    .join(',');

  const [snapshot, setSnapshot] = useState<Map<string, PlatformUserAvatarFields | undefined>>(
    () => new Map(),
  );

  useEffect(() => {
    const normalizedIds = userIdsKey ? userIdsKey.split(',') : [];

    const initial = new Map<string, PlatformUserAvatarFields | undefined>();
    for (const id of normalizedIds) {
      const cached = apolloClient.readFragment<PlatformUserAvatarFields>({
        id: apolloClient.cache.identify({ __typename: 'PlatformUser', id }),
        fragment: PLATFORM_USER_AVATAR_FRAGMENT,
      });
      initial.set(id, cached ?? undefined);
    }
    setSnapshot(initial);

    const subscriptions = normalizedIds.map((id) =>
      apolloClient.cache
        .watchFragment<PlatformUserAvatarFields>({
          fragment: PLATFORM_USER_AVATAR_FRAGMENT,
          from: { __typename: 'PlatformUser', id },
        })
        .subscribe((result) => {
          setSnapshot((prev) => {
            const next = new Map(prev);
            next.set(
              id,
              result.complete ? result.data : (result.data as PlatformUserAvatarFields),
            );
            return next;
          });
        }),
    );

    return () => {
      subscriptions.forEach((sub) => sub.unsubscribe());
    };
  }, [apolloClient, userIdsKey]);

  return useMemo(() => snapshot, [snapshot]);
}
